import { useEffect, useState } from 'react'
import { cn } from '../lib/cn'
import { useUiStrings } from '../i18n'
import logo from '../assets/logo-vitroconnect.svg'

export type Language = 'de' | 'en'

export function Logo({
  className,
  href = 'https://www.vitroconnect.com',
}: {
  className?: string
  href?: string | null
}) {
  const t = useUiStrings();
  const img = (
    <img
      src={logo}
      alt="vitroconnect"
      width={214}
      height={48}
      className={cn('h-8 w-auto sm:h-9', className)}
    />
  )

  if (!href) return img

  return (
    <a href={href} rel="noopener noreferrer" aria-label={t.homepage}>
      {img}
    </a>
  )
}

export function LanguageSwitch({
  value,
  onChange,
  className,
}: {
  value: Language
  onChange: (next: Language) => void
  className?: string
}) {
  const t = useUiStrings();
  const languages: Language[] = ['de', 'en']

  return (
    <div role="group" aria-label={t.chooseLanguage} className={cn('vcn-lang', className)}>
      {languages.map((lang) => (
        <button
          key={lang}
          type="button"
          lang={lang}
          aria-pressed={lang === value}
          onClick={() => onChange(lang)}
          className="vcn-lang__option"
        >
          {lang}
        </button>
      ))}
    </div>
  )
}

/**
 * Kopfzeile: Logo links, Sprachumschalter rechts.
 * Wird beim Scrollen leicht abgedunkelt, damit sie über hellen
 * Hintergrundbereichen lesbar bleibt.
 */
export function SiteHeader({
  language,
  onLanguageChange,
  className,
}: {
  language: Language
  onLanguageChange: (next: Language) => void
  className?: string
}) {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
        scrolled && 'bg-black/15 backdrop-blur-md',
        className,
      )}
    >
      {/* Im Entwurf steht der Sprachumschalter mobil oben rechts und das Logo
          mittig darunter; ab sm liegen beide nebeneinander. */}
      <div className="mx-auto w-full max-w-[112rem] px-6 py-4 sm:px-10">
        <div className="flex justify-end sm:hidden">
          <LanguageSwitch value={language} onChange={onLanguageChange} />
        </div>
        <div className="mt-3 flex justify-center sm:mt-0 sm:hidden">
          <Logo />
        </div>

        <div className="hidden items-center justify-between sm:flex">
          <Logo />
          <LanguageSwitch value={language} onChange={onLanguageChange} />
        </div>
      </div>
    </header>
  )
}
